import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../theme';
import { pickRecorderMime } from '../q/huntListen';

/** Längsta video: ~2,5 Mbit/s i 30 s ≈ 10 MB, under Cloud Runs gräns på 32 MB per anrop. */
export const VIDEO_MAX_MS = 30_000;
const VIDEO_BITS = 2_500_000;

/**
 * I2: spela in en kort video till jaktchatten (och markens album) med telefonens kamera i webbvyn
 * (getUserMedia + MediaRecorder; iOS ger mp4). Jaktens lyssning ska vara pausad innan den öppnas, eftersom
 * WebKit då tar mikrofonen (regel 1 i ios/App/App/QVoicePlugin.swift).
 */
export function HuntVideoRecorder({ onClose, onSend }: {
  onClose: () => void;
  onSend: (blob: Blob, durationMs: number) => void;
}) {
  const T = useTheme();
  const liveRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const [facing, setFacing] = useState<'environment' | 'user'>('environment');
  const [state, setState] = useState<'starting' | 'ready' | 'recording' | 'done' | 'error'>('starting');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<{ blob: Blob; url: string; durationMs: number } | null>(null);

  const release = () => { streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null; };

  useEffect(() => {
    if (result) return;
    let cancelled = false;
    setState('starting');
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } },
      audio: true,
    }).then(stream => {
      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
      release();
      streamRef.current = stream;
      if (liveRef.current) { liveRef.current.srcObject = stream; void liveRef.current.play().catch(() => {}); }
      setState('ready');
    }).catch(err => {
      if (cancelled) return;
      setError(err?.name === 'NotAllowedError' ? 'Kameran eller mikrofonen är inte tillåten för Qvenari.' : 'Kameran kunde inte startas.');
      setState('error');
    });
    return () => { cancelled = true; };
  }, [facing, result]);

  useEffect(() => () => { recRef.current?.state === 'recording' && recRef.current.stop(); release(); }, []);
  useEffect(() => () => { if (result) URL.revokeObjectURL(result.url); }, [result]);

  useEffect(() => {
    if (state !== 'recording') return;
    const started = Date.now();
    const t = setInterval(() => {
      const ms = Date.now() - started;
      setElapsed(ms);
      if (ms >= VIDEO_MAX_MS && recRef.current?.state === 'recording') recRef.current.stop();
    }, 200);
    return () => clearInterval(t);
  }, [state]);

  const start = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const mime = pickRecorderMime('video');
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: VIDEO_BITS, audioBitsPerSecond: 64_000 });
    } catch {
      setError('Telefonen kan inte spela in video här.');
      setState('error');
      return;
    }
    const chunks: Blob[] = [];
    const started = Date.now();
    rec.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunks, { type: (rec.mimeType || mime || 'video/mp4').split(';')[0] });
      release();
      setResult({ blob, url: URL.createObjectURL(blob), durationMs: Date.now() - started });
      setState('done');
    };
    recRef.current = rec;
    rec.start(1000);
    setElapsed(0);
    setState('recording');
  };

  const retake = () => { setResult(null); setElapsed(0); };
  const secs = Math.floor(elapsed / 1000);
  const btn = (primary: boolean): React.CSSProperties => ({
    flex: 1, minHeight: 46, borderRadius: 999, border: primary ? 'none' : `1.5px solid ${T.border}`,
    background: primary ? T.amber : 'transparent', color: primary ? '#0a0a0a' : '#fff',
    fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
  });

  return (
    <div role="dialog" aria-label="Spela in video" style={{
      position: 'fixed', inset: 0, zIndex: 10050, background: '#000', display: 'flex', flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', color: '#fff' }}>
        <button type="button" onClick={onClose} aria-label="Stäng" style={{ background: 'none', border: 'none', color: '#fff', padding: 6, cursor: 'pointer' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
        </button>
        <span style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {state === 'recording' ? `● ${secs} / ${VIDEO_MAX_MS / 1000} s` : 'Video till jaktchatten'}
        </span>
        {state === 'ready' ? (
          <button type="button" onClick={() => setFacing(f => (f === 'environment' ? 'user' : 'environment'))} aria-label="Byt kamera" style={{ background: 'none', border: 'none', color: '#fff', padding: 6, cursor: 'pointer' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M20 7h-3l-2-3H9L7 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" /><path d="M9 13a3 3 0 015-2.2M15 13a3 3 0 01-5 2.2" /></svg>
          </button>
        ) : <span style={{ width: 34 }} />}
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {result ? (
          <video src={result.url} controls playsInline style={{ maxWidth: '100%', maxHeight: '100%' }} />
        ) : state === 'error' ? (
          <div style={{ color: '#fff', padding: 24, textAlign: 'center', fontSize: 15, lineHeight: 1.5 }}>{error}</div>
        ) : (
          <video ref={liveRef} muted playsInline autoPlay style={{ maxWidth: '100%', maxHeight: '100%', transform: facing === 'user' ? 'scaleX(-1)' : undefined }} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', padding: '14px 16px 18px' }}>
        {result ? (
          <>
            <button type="button" onClick={retake} style={btn(false)}>Ta om</button>
            <button type="button" onClick={() => onSend(result.blob, result.durationMs)} style={btn(true)}>Skicka</button>
          </>
        ) : state === 'ready' || state === 'recording' ? (
          <button type="button" onClick={() => (state === 'recording' ? recRef.current?.stop() : start())}
            aria-label={state === 'recording' ? 'Stoppa inspelningen' : 'Börja spela in'}
            style={{ width: 72, height: 72, borderRadius: '50%', border: '4px solid #fff', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ width: state === 'recording' ? 26 : 56, height: state === 'recording' ? 26 : 56, borderRadius: state === 'recording' ? 6 : '50%', background: '#E53935', transition: 'all 0.15s' }} />
          </button>
        ) : (
          <button type="button" onClick={onClose} style={btn(false)}>Stäng</button>
        )}
      </div>
    </div>
  );
}
